import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, Sun, Percent, Euro, CalendarRange } from "lucide-react";

const DAYS = [["0", "Lun"], ["1", "Mar"], ["2", "Mer"], ["3", "Jeu"], ["4", "Ven"], ["5", "Sam"], ["6", "Dim"]];
const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const priceFor = (base, s) => (s.mode === "fixed"
  ? Number(s.value || 0)
  : Number(base || 0) * (1 + Number(s.value || 0) / 100));

export const ReservationSeasons = ({ cfg, setCfg, saveCfg, saving, unitTypes = [], basePrice = 0 }) => {
  const seasons = cfg.seasons || [];
  const wk = cfg.weekend || { enabled: false, days: [4, 5], mode: "percent", value: 0 };

  const setSeason = (i, patch) => {
    const next = seasons.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setCfg({ ...cfg, seasons: next });
  };
  const addSeason = () => setCfg({
    ...cfg,
    seasons: [...seasons, {
      id: "", label: "Haute saison", date_from: "07-01", date_to: "08-31",
      mode: "percent", value: 30, min_nights: 0, unit_types: [], active: true,
    }],
  });
  const removeSeason = (i) => setCfg({ ...cfg, seasons: seasons.filter((_, idx) => idx !== i) });
  const setWk = (patch) => setCfg({ ...cfg, weekend: { ...wk, ...patch } });
  const toggleType = (i, t) => {
    const cur = seasons[i].unit_types || [];
    setSeason(i, { unit_types: cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t] });
  };

  return (
    <div className="space-y-5" data-testid="cr-seasons">
      {/* ---------- Week-end ---------- */}
      <div className="border border-[#DEE2E6] bg-white p-4" data-testid="cr-weekend">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] flex items-center gap-1 mb-2">
          <Sun className="h-3 w-3" />Supplément week-end
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <button type="button" data-testid="cr-weekend-enabled"
                  onClick={() => setWk({ enabled: !wk.enabled })}
                  className={`h-10 px-3 text-[11px] uppercase tracking-wider font-bold border ${
                    wk.enabled ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
            {wk.enabled ? "✓ Activé" : "✗ Désactivé"}
          </button>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Jours concernés</Label>
            <div className="flex gap-1">
              {DAYS.map(([v, l]) => {
                const on = (wk.days || []).includes(Number(v));
                return (
                  <button key={v} type="button" data-testid={`cr-weekend-day-${v}`}
                          onClick={() => setWk({
                            days: on ? wk.days.filter((d) => d !== Number(v)) : [...(wk.days || []), Number(v)],
                          })}
                          className={`h-10 w-11 text-[10px] uppercase font-bold border ${
                            on ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                    {l}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Supplément</Label>
            <div className="flex">
              <Input type="number" step="0.01" value={wk.value ?? 0} className="w-[110px]"
                     data-testid="cr-weekend-value"
                     onChange={(e) => setWk({ value: parseFloat(e.target.value) || 0 })} />
              <button type="button" data-testid="cr-weekend-mode"
                      onClick={() => setWk({ mode: wk.mode === "percent" ? "fixed" : "percent" })}
                      className="h-10 w-11 grid place-items-center border border-l-0 border-[#DEE2E6] hover:bg-[#F1F3F5]">
                {wk.mode === "fixed" ? <Euro className="h-4 w-4" /> : <Percent className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {basePrice > 0 && wk.enabled && (
            <div className="text-[11px] text-[#495057]" data-testid="cr-weekend-preview">
              {eur(basePrice)} → <b>{eur(priceFor(basePrice, wk))}</b> la nuit du week-end
            </div>
          )}
        </div>
        <div className="text-[11px] text-[#868E96] mt-2">
          Le supplément s'applique après le tarif de saison. Cliquez sur % ou € pour changer de mode.
        </div>
      </div>

      {/* ---------- Saisons ---------- */}
      <div className="border border-[#DEE2E6] bg-white p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] flex items-center gap-1">
            <CalendarRange className="h-3 w-3" />Périodes tarifaires
          </div>
          <Button size="sm" variant="outline" onClick={addSeason} data-testid="cr-season-add" className="ml-auto">
            <Plus className="h-4 w-4 mr-1" />Ajouter une période
          </Button>
        </div>

        {seasons.length === 0 ? (
          <div className="border border-dashed border-[#DEE2E6] p-6 text-center text-xs text-[#868E96]"
               data-testid="cr-seasons-empty">
            Aucune période : le tarif de base de chaque unité s'applique toute l'année.
            Ajoutez « Haute saison » pour facturer plus cher en juillet-août.
          </div>
        ) : (
          <div className="space-y-3" data-testid="cr-seasons-list">
            {seasons.map((s, i) => (
              <div key={s.id || i} className="border border-[#DEE2E6] p-3" data-testid={`cr-season-${i}`}>
                <div className="grid md:grid-cols-[1fr_100px_100px_130px_110px_auto] gap-2 items-end">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Nom</Label>
                    <Input value={s.label || ""} data-testid={`cr-season-label-${i}`}
                           onChange={(e) => setSeason(i, { label: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Du (MM-JJ)</Label>
                    <Input value={s.date_from || ""} placeholder="07-01" data-testid={`cr-season-from-${i}`}
                           onChange={(e) => setSeason(i, { date_from: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Au (MM-JJ)</Label>
                    <Input value={s.date_to || ""} placeholder="08-31" data-testid={`cr-season-to-${i}`}
                           onChange={(e) => setSeason(i, { date_to: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Tarif</Label>
                    <div className="flex">
                      <Input type="number" step="0.01" value={s.value ?? 0} data-testid={`cr-season-value-${i}`}
                             onChange={(e) => setSeason(i, { value: parseFloat(e.target.value) || 0 })} />
                      <button type="button" data-testid={`cr-season-mode-${i}`}
                              onClick={() => setSeason(i, { mode: s.mode === "percent" ? "fixed" : "percent" })}
                              className="h-10 w-11 shrink-0 grid place-items-center border border-l-0 border-[#DEE2E6] hover:bg-[#F1F3F5]">
                        {s.mode === "fixed" ? <Euro className="h-4 w-4" /> : <Percent className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Nuits mini</Label>
                    <Input type="number" min="0" value={s.min_nights ?? 0} data-testid={`cr-season-minnights-${i}`}
                           onChange={(e) => setSeason(i, { min_nights: parseInt(e.target.value) || 0 })} />
                  </div>
                  <Button variant="outline" size="icon" onClick={() => removeSeason(i)}
                          data-testid={`cr-season-del-${i}`} className="text-[#E60000]">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {unitTypes.length > 0 && (
                  <div className="mt-2">
                    <Label className="text-[10px] uppercase tracking-wider">
                      Types concernés (aucun = tous)
                    </Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {unitTypes.map((t) => {
                        const on = (s.unit_types || []).includes(t);
                        return (
                          <button key={t} type="button" data-testid={`cr-season-type-${i}-${t}`}
                                  onClick={() => toggleType(i, t)}
                                  className={`text-[10px] uppercase font-bold px-2 py-1 border ${
                                    on ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                            {t.replace(/_/g, " ")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="text-[11px] text-[#495057] mt-2" data-testid={`cr-season-preview-${i}`}>
                  {basePrice > 0
                    ? <>Une nuit à {eur(basePrice)} sera facturée <b>{eur(priceFor(basePrice, s))}</b>
                        {s.min_nights > 0 ? <> · séjour minimum {s.min_nights} nuit(s)</> : null}</>
                    : "Renseignez un tarif de base sur vos unités pour voir l'aperçu."}
                </div>
              </div>
            ))}
          </div>
        )}

        <Button onClick={() => saveCfg()} disabled={saving} className="mt-4 bg-[#002FA7] text-white"
                data-testid="cr-seasons-save">
          <Save className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : "Enregistrer les tarifs"}
        </Button>
      </div>
    </div>
  );
};
